import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Camera, Zap, Users, Calendar, Mail, Instagram, Star, Check, Phone, MapPin, Menu, X, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { href: "#services", label: "Services" },
  { href: "#gallery", label: "Gallery" },
  { href: "#testimonials", label: "Reviews" },
  { href: "#pricing", label: "Pricing" },
  { href: "#contact", label: "Contact" }];


  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ?
      "bg-white/90 backdrop-blur-lg shadow-sm border-b border-zinc-100" :
      "bg-transparent"}`
      }>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#" className="flex items-center gap-2" data-testid="logo">
            <img
              src="https://customer-assets.emergentagent.com/job_selfie-spot-1/artifacts/3n633f12_IMG_0252.PNG"
              alt="The Selfie Spot AZ Photo Booth"
              className="h-12 w-12 object-contain" />

            <span className="font-heading text-xl font-bold tracking-tight text-zinc-900">
              Selfie Spot AZ
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors">

                {link.label}
              </a>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <a href="#contact">
              <Button
                data-testid="book-now-header-btn"
                className="h-10 px-6 rounded-full bg-zinc-900 text-white hover:bg-zinc-800 font-medium btn-shine">

                Book Now
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>

            {isMobileMenuOpen ?
            <X className="w-6 h-6 text-zinc-900" /> :

            <Menu className="w-6 h-6 text-zinc-900" />
            }
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen &&
        <nav
          data-testid="mobile-nav"
          className="md:hidden py-4 border-t border-zinc-100 bg-white">

            {navLinks.map((link) =>
          <a
            key={link.href}
            href={link.href}
            className="block py-3 text-base font-medium text-zinc-600 hover:text-zinc-900"
            onClick={() => setIsMobileMenuOpen(false)}>

                {link.label}
              </a>
          )}
            <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="mt-4 w-full h-12 rounded-full bg-zinc-900 text-white hover:bg-zinc-800">
                Book Now
              </Button>
            </a>
          </nav>
        }
      </div>
    </header>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 hero-pattern overflow-hidden">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-8 opacity-0 animate-slide-in-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-100 rounded-full">
              <Sparkles className="w-4 h-4 text-violet-600" />
              <span className="text-sm font-medium text-zinc-700">
                Arizona's Premier Photo Experience
              </span>
            </div>
            
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-zinc-900 leading-[1.1]">
              Capture Every
              <span className="block gradient-text">Unforgettable</span>
              Moment
            </h1>
            
            <p className="text-lg md:text-xl text-zinc-600 leading-relaxed max-w-lg">
              Transform your events with our premium photo booth experiences. 
              From weddings to corporate events, we bring the fun and create lasting memories.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#contact">
                <Button
                  data-testid="hero-book-btn"
                  className="h-14 px-8 rounded-full bg-zinc-900 text-white hover:bg-zinc-800 text-base font-medium btn-shine shadow-lg hover:shadow-xl transition-shadow">

                  Book Your Date
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href="#services">
                <Button
                  data-testid="hero-services-btn"
                  variant="outline"
                  className="h-14 px-8 rounded-full border-2 border-zinc-200 hover:border-zinc-900 text-base font-medium">

                  Explore Services
                </Button>
              </a>
            </div>
            
            <div className="flex items-center gap-8 pt-4">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) =>
                  <Star
                    key={i}
                    className="w-5 h-5 fill-yellow-400 text-yellow-400" />

                  )}
                </div>
                <span className="text-sm font-medium text-zinc-700">5.0</span>
              </div>
              <div className="text-sm text-zinc-500">
                <span className="font-semibold text-zinc-700">500+</span> Events Completed
              </div>
            </div>
          </div>
          
          <div className="relative opacity-0 animate-slide-in-right stagger-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 rounded-3xl blur-3xl" />
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_selfie-spot-1/artifacts/swuf06zv_IMG_1155.webp"
                alt="The Selfie Spot AZ Photobooth"
                className="relative w-full h-[500px] lg:h-[600px] object-cover rounded-3xl shadow-2xl" />
            </div>
            <div className="mt-4 bg-white/90 backdrop-blur-lg rounded-2xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Next Available</p>
                  <p className="font-heading font-semibold text-zinc-900">This Weekend</p>
                </div>
                <a href="#contact">
                  <Button className="h-10 px-4 rounded-full bg-zinc-900 text-white hover:bg-zinc-800 text-sm">
                    Reserve
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Services Section
const ServicesSection = () => {
  const services = [
  {
    title: "Digital iPad Photo Booth",
    description: "Modern, sleek iPad photo booth experience with premium props, instant prints, and digital sharing. Perfect for weddings, parties, and corporate events.",
    image: "https://images.pexels.com/photos/6519241/pexels-photo-6519241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: ["Instant Prints", "Digital Gallery", "Social Sharing", "Custom Branding", "Props Included"]
  }];


  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-zinc-50">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-medium tracking-wider uppercase text-violet-600 mb-4">
            Our Services
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-zinc-900 mb-6">
            Premium Photo Experiences
          </h2>
          <p className="text-lg text-zinc-600 leading-relaxed">
            Our sleek Digital iPad Photo Booth brings fun and style to any event.
          </p>
        </div>

        <div className="grid md:grid-cols-1 max-w-2xl mx-auto gap-8">
          {services.map((service, index) =>
          <div
            key={service.title}
            data-testid={`service-card-${index}`}
            className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 card-lift">

              <div className="relative h-64 img-zoom">
                <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover" />

                <div className="absolute inset-0 service-card-overlay !bg-[rgba(255,255,255,0)]" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-semibold text-zinc-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-zinc-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature) =>
                <span
                  key={feature}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-zinc-100 rounded-full text-sm text-zinc-700">

                      <Check className="w-3 h-3 text-violet-600" />
                      {feature}
                    </span>
                )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Gallery Section
const GallerySection = () => {
  const images = [
  "https://wallpapers.com/images/file/photobooth-pictures-midr6x7xr58qqovi.jpg",
  "https://projectgo.pro/wp-content/uploads/2023/04/photo-booth-1.jpg",
  "https://i.etsystatic.com/39655187/r/il/3a4172/5528526889/il_1140xN.5528526889_deyv.jpg",
  "https://images.pexels.com/photos/5716283/pexels-photo-5716283.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/6519358/pexels-photo-6519358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"];


  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 md:py-32">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-medium tracking-wider uppercase text-violet-600 mb-4">
            Our Work
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-zinc-900 mb-6">
            Memories We've Captured
          </h2>
          <p className="text-lg text-zinc-600 leading-relaxed">
            Browse through moments from weddings, corporate events, birthday parties, and more.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {images.map((image, index) =>
          <div
            key={index}
            data-testid={`gallery-image-${index}`}
            className={`gallery-item rounded-2xl overflow-hidden ${
            index === 0 || index === 5 ? "md:row-span-2" : ""}`
            }>

              <img
              src={image}
              alt={`Gallery image ${index + 1}`}
              className={`w-full object-cover ${
              index === 0 || index === 5 ? "h-64 md:h-full" : "h-48 md:h-64"}`
              } />

            </div>
          )}
        </div>
      </div>
    </section>);

};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
  {
    name: "Sarah & Michael",
    event: "Wedding",
    text: "Selfie Spot AZ made our wedding reception unforgettable! The Digital Photo booth was a huge hit with all our guests. The photos came out amazing!",
    rating: 5
  },
  {
    name: "Jennifer Thompson",
    event: "Corporate Event",
    text: "Professional, punctual, and so much fun! Our company holiday party was elevated to the next level. Highly recommend for any corporate event.",
    rating: 5
  },
  {
    name: "Maria Rodriguez",
    event: "Quinceañera",
    text: "The Digital photo booth was absolutely magical! My daughter and her friends couldn't stop taking pictures. Worth every penny!",
    rating: 5
  }];


  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-24 md:py-32 bg-zinc-50">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-medium tracking-wider uppercase text-violet-600 mb-4">
            Testimonials
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-zinc-900 mb-6">
            What Our Clients Say
          </h2>
          <p className="text-lg text-zinc-600 leading-relaxed">
            Don't just take our word for it. Here's what event planners and hosts have to say.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) =>
          <div
            key={index}
            data-testid={`testimonial-card-${index}`}
            className="testimonial-card rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">

              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) =>
              <Star
                key={i}
                className="w-5 h-5 fill-yellow-400 text-yellow-400" />

              )}
              </div>
              <p className="text-zinc-700 leading-relaxed mb-6">
                "{testimonial.text}"
              </p>
              <div>
                <p className="font-semibold text-zinc-900">{testimonial.name}</p>
                <p className="text-sm text-zinc-500">{testimonial.event}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Pricing Section
const PricingSection = () => {
  const packages = [
  {
    name: "Essential",
    price: "399",
    duration: "3 Hours",
    features: [
    "Digital iPad Photo Booth",
    "Unlimited Sessions",
    "Digital Gallery",
    "Basic Props Pack",
    "Custom Backdrop",
    "On-site Attendant"],

    popular: false
  },
  {
    name: "Premium",
    price: "699",
    duration: "5 Hours",
    features: [
    "Digital iPad Photo Booth",
    "Unlimited Sessions",
    "Instant Prints & Digital",
    "Premium Props Collection",
    "Custom Overlays",
    "Social Media Sharing",
    "Guest Book Option",
    "On-site Attendant"],

    popular: true
  },
  {
    name: "Ultimate",
    price: "799",
    duration: "6 Hours",
    features: [
    "Digital iPad Photo Booth",
    "Unlimited Everything",
    "Same-Day Gallery Delivery",
    "Luxury Props & Backdrops",
    "Custom Branding",
    "Physical Photo Prints",
    "Red Carpet Setup",
    "2 On-site Attendants"],

    popular: false
  }];


  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-medium tracking-wider uppercase text-violet-600 mb-4">
            Pricing
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-zinc-900 mb-6">
            Transparent Pricing
          </h2>
          <p className="text-lg text-zinc-600 leading-relaxed">
            Choose the perfect package for your event. All packages include setup, breakdown, and travel within Phoenix metro.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {packages.map((pkg, index) =>
          <div
            key={pkg.name}
            data-testid={`pricing-card-${index}`}
            className={`relative rounded-2xl p-8 ${
            pkg.popular ?
            "pricing-popular bg-white shadow-2xl scale-105" :
            "bg-white border border-zinc-200 shadow-sm"}`
            }>

              {pkg.popular &&
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
            }
              <div className="text-center mb-8">
                <h3 className="font-heading text-xl font-semibold text-zinc-900 mb-2">
                  {pkg.name}
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-zinc-900">${pkg.price}</span>
                </div>
                <p className="text-sm text-zinc-500 mt-1">{pkg.duration}</p>
              </div>
              <ul className="space-y-4 mb-8">
                {pkg.features.map((feature) =>
              <li key={feature} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-700">{feature}</span>
                  </li>
              )}
              </ul>
              <a href="#contact">
                <Button
                data-testid={`pricing-btn-${index}`}
                className={`w-full h-12 rounded-full font-medium ${
                pkg.popular ?
                "bg-zinc-900 text-white hover:bg-zinc-800" :
                "bg-zinc-100 text-zinc-900 hover:bg-zinc-200"}`
                }>

                  Get Started
                </Button>
              </a>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventType: "",
    eventDate: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        eventDate: "",
        message: ""
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-zinc-50">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm font-medium tracking-wider uppercase text-violet-600 mb-4">
              Get In Touch
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-zinc-900 mb-6">
              Let's Create Magic Together
            </h2>
            <p className="text-lg text-zinc-600 leading-relaxed mb-8">
              Ready to book or have questions? Fill out the form and we'll get back to you within 24 hours. 
              Or reach out directly through our contact info below.
            </p>

            <div className="space-y-6">
              <a
                href="tel:+14805551234"
                className="flex items-center gap-4 group"
                data-testid="contact-phone">

                <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center group-hover:bg-zinc-200 transition-colors">
                  <Phone className="w-5 h-5 text-zinc-700" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Call Us</p>
                  <p className="font-medium text-zinc-900">(623) 282-0245</p>
                </div>
              </a>

              <a
                href="mailto:hello@selfiespotaz.com"
                className="flex items-center gap-4 group"
                data-testid="contact-email">

                <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center group-hover:bg-zinc-200 transition-colors">
                  <Mail className="w-5 h-5 text-zinc-700" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Email Us</p>
                  <p className="font-medium text-zinc-900">selfiespotaz@gmail.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-zinc-700" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Serving</p>
                  <p className="font-medium text-zinc-900">Phoenix Metro Area, AZ</p>
                </div>
              </div>

              <a
                href="https://instagram.com/selfiespotaz"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-instagram">

                <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center group-hover:bg-zinc-200 transition-colors">
                  <Instagram className="w-5 h-5 text-zinc-700" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Follow Us</p>
                  <p className="font-medium text-zinc-900">@selfiespotaz</p>
                </div>
              </a>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="h-12 rounded-lg border-zinc-200 focus:border-zinc-900 focus:ring-0 bg-zinc-50/50"
                    data-testid="contact-name-input" />

                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="h-12 rounded-lg border-zinc-200 focus:border-zinc-900 focus:ring-0 bg-zinc-50/50"
                    data-testid="contact-email-input" />

                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(480) 555-1234"
                    className="h-12 rounded-lg border-zinc-200 focus:border-zinc-900 focus:ring-0 bg-zinc-50/50"
                    data-testid="contact-phone-input" />

                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Event Type
                  </label>
                  <Input
                    type="text"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    placeholder="Wedding, Corporate, etc."
                    className="h-12 rounded-lg border-zinc-200 focus:border-zinc-900 focus:ring-0 bg-zinc-50/50"
                    data-testid="contact-eventtype-input" />

                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Event Date
                </label>
                <Input
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  className="h-12 rounded-lg border-zinc-200 focus:border-zinc-900 focus:ring-0 bg-zinc-50/50"
                  data-testid="contact-eventdate-input" />

              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Tell Us About Your Event
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Share details about your event, guest count, venue, etc."
                  className="rounded-lg border-zinc-200 focus:border-zinc-900 focus:ring-0 bg-zinc-50/50 resize-none"
                  data-testid="contact-message-input" />

              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 rounded-full bg-zinc-900 text-white hover:bg-zinc-800 font-medium btn-shine"
                data-testid="contact-submit-btn">

                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="py-12 bg-zinc-900 text-white">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_selfie-spot-1/artifacts/3n633f12_IMG_0252.PNG"
              alt="The Selfie Spot AZ Photo Booth"
              className="h-10 w-10 object-contain" />

            <span className="font-heading text-lg font-bold tracking-tight">
              Selfie Spot AZ
            </span>
          </div>

          <nav className="flex flex-wrap justify-center gap-6">
            <a href="#services" className="text-sm text-zinc-400 hover:text-white transition-colors">
              Services
            </a>
            <a href="#gallery" className="text-sm text-zinc-400 hover:text-white transition-colors">
              Gallery
            </a>
            <a href="#testimonials" className="text-sm text-zinc-400 hover:text-white transition-colors">
              Reviews
            </a>
            <a href="#pricing" className="text-sm text-zinc-400 hover:text-white transition-colors">
              Pricing
            </a>
            <a href="#contact" className="text-sm text-zinc-400 hover:text-white transition-colors">
              Contact
            </a>
          </nav>

          <div className="flex items-center gap-4">
            <a
              href="https://instagram.com/selfiespotaz"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center hover:bg-zinc-700 transition-colors"
              data-testid="footer-instagram">

              <Instagram className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-zinc-800 text-center">
          <p className="text-sm text-zinc-500">
            © {new Date().getFullYear()} Selfie Spot AZ. All rights reserved.
          </p>
        </div>
      </div>
    </footer>);

};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <GallerySection />
        <TestimonialsSection />
        <PricingSection />
        <ContactSection />
      </main>
      <Footer />
    </div>);

}

export default App;